import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import type {SocialAccount} from '@Components/social-media/account.vo';
import {FieldSpacer} from '@Components/social-media-wizard/components/field-spacer';
import {Icon} from '@Components/icon-v2';
import {IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {Checkbox, CHECKBOX_TYPE} from '@Components/checkbox';
import {ToggleSwitch} from '@Components/toggle-switch-v2';
import {ClickableDiv} from '@Components/clickable-div';
import {
  updatePublishingParamDisableComment,
  updatePublishingParamDisableDuet,
  updatePublishingParamDisableStitch,
  updatePublishingParamIsPromotingThirdParty,
  updatePublishingParamIsSelfBrand,
  updateWizardSocialAccount,
} from '@Components/social-media-wizard/social-media-wizard-reducer';
import {TiktokPrivacySelector} from '@Components/social-media-wizard/components/tiktok-privacy-selector';
import {SkeletonBrick} from '@Components/skeleton-brick';
import {usePostPublishingParamsForAccount} from '@Components/social-media/social-media.hooks';
import type {TikTokPublishingParams} from '@Components/social-media/publish-params.types';
import {useWizardDesignsGraphicsMeta} from '@Components/social-media-wizard/social-media-wizard.hooks';
import styles from './tiktok-publishing-options.module.scss';
import {useAppDispatch} from '@/hooks';
import type {TikTokPrivacySettings} from '@/components/social-media/post.vo';
import {TikTokPrivateAccountPrivacySettings, TikTokPublicAccountPrivacySettings} from '@/components/social-media/post.vo';

interface TiktokPublishingOptionsProps {
  account: SocialAccount;
}

interface TikTokUpdateResponse {
  account: SocialAccount;
}

export function TiktokPublishingOptions({account}: TiktokPublishingOptionsProps): ReactElement<any> | null {
  const {tikTokMeta} = account;
  const [disableComment, setDisableComment] = useState(tikTokMeta ? tikTokMeta.isCommentDisabled : false);
  const [disableStitch, setDisableStitch] = useState(tikTokMeta ? tikTokMeta.isStitchDisabled : false);
  const [disableDuet, setDisableDuet] = useState(tikTokMeta ? tikTokMeta.isDuetDisabled : false);
  const [contentDisclosureToggle, setContentDisclosureToggle] = useState(false);
  const [isSelfBrand, setIsSelfBrand] = useState(false);
  const [isPromotingThirdParty, setIsPromotingThirdParty] = useState(false);
  const [isBrandSwitchDisabled, setIsBrandSwitchDisabled] = useState(false);
  const [isTikTokAPIQueried, setIsTikTokAPIQueried] = useState(false);
  const dispatch = useAppDispatch();
  const publishingParams = usePostPublishingParamsForAccount(account.id) as TikTokPublishingParams;
  const designsMeta = useWizardDesignsGraphicsMeta();

  const onDisableComment = (): void => {
    const newDisableComment = !disableComment;

    setDisableComment(newDisableComment);
    dispatch(
      updatePublishingParamDisableComment({
        idSocialMediaAccount: account.id,
        disableComment: newDisableComment,
      })
    );
  };

  const onDisableStitch = (): void => {
    const newDisableStitch = !disableStitch;

    setDisableStitch(newDisableStitch);
    dispatch(
      updatePublishingParamDisableStitch({
        idSocialMediaAccount: account.id,
        disableStitch: newDisableStitch,
      })
    );
  };

  const onDisableDuet = (): void => {
    const newDisableDuet = !disableDuet;

    setDisableDuet(!disableDuet);
    dispatch(
      updatePublishingParamDisableDuet({
        idSocialMediaAccount: account.id,
        disableDuet: newDisableDuet,
      })
    );
  };

  const doRefreshTikTokOptions = async (): Promise<void> => {
    const data = (await window.PMW.readLocal(`socialmedia/updateTikTokAccountMeta/${account.id}`)) as TikTokUpdateResponse;
    if (!data.account) {
      return;
    }

    dispatch(updateWizardSocialAccount(data.account));
    setIsTikTokAPIQueried(true);
  };

  const onIsSelfBrand = (): void => {
    const newIsSelfBrand = !isSelfBrand;

    setIsSelfBrand(newIsSelfBrand);
    dispatch(
      updatePublishingParamIsSelfBrand({
        idSocialMediaAccount: account.id,
        isSelfBrand: newIsSelfBrand,
      })
    );
  };

  const onIsPromotingThirdParty = (): void => {
    const newIsPromotingThirdParty = !isPromotingThirdParty;

    setIsPromotingThirdParty(!isPromotingThirdParty);
    dispatch(
      updatePublishingParamIsPromotingThirdParty({
        idSocialMediaAccount: account.id,
        isPromotingThirdParty: newIsPromotingThirdParty,
      })
    );
  };

  const onContentDisclosureToggle = (): void => {
    setContentDisclosureToggle(!contentDisclosureToggle);
  };

  const isThereAnyVideoPoster = (): boolean => {
    const posterIds = Object.keys(designsMeta);

    for (let i = 0; i < posterIds.length; i++) {
      if (designsMeta[posterIds[i]].isVideo) {
        return true;
      }
    }

    return false;
  };

  const getPostMetaOptions = (): ReactElement<any> => {
    return (
      <div className="flex-h-row flex-content-between flex-wrap spacing-p-t-2 spacing-p-b-2">
        <div className="flex-center">
          <Icon icon="icon-group" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text val={window.i18next.t('pmwjs_allow_users_to')} size={TextSize.XSMALL} />
        </div>
        <div className="flex-h-row">
          {getAllowCommentElement()}
          {isThereAnyVideoPoster() ? getAllowDuetElement() : null}
          {isThereAnyVideoPoster() ? getAllowStitchElement() : null}
        </div>
      </div>
    );
  };

  const getAllowDuetElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row spacing-m-r-4 _cursor-pointer" onClick={onDisableDuet}>
        <Checkbox isChecked={disableDuet} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_duet')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getAllowStitchElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row _cursor-pointer" onClick={onDisableStitch}>
        <Checkbox isChecked={disableStitch} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_stitch')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getAllowCommentElement = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={65} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return (
      <ClickableDiv className="flex-h-row spacing-m-r-4 _cursor-pointer" onClick={onDisableComment}>
        <Checkbox isChecked={disableComment} type={CHECKBOX_TYPE.PRIMARY} className="spacing-m-r-3" />
        <Text val={window.i18next.t('pmwjs_comment')} size={TextSize.XSMALL} className="content-body" />
      </ClickableDiv>
    );
  };

  const getContentDisclosureToggle = (): ReactElement<any> => {
    return (
      <div className="flex-h-row flex-content-between">
        <div className="flex-center">
          <Icon icon="icon-video" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text val={window.i18next.t('pmwjs_content_disclosure_and_ads')} size={TextSize.XSMALL} />
        </div>
        <div>{getToggleSwitch()}</div>
      </div>
    );
  };

  const getToggleSwitch = (): ReactElement<any> => {
    if (!isTikTokAPIQueried) {
      return <SkeletonBrick width={60} height={30} className="spacing-m-r-4 radius-4" />;
    }

    return <ToggleSwitch isDisabled={isBrandSwitchDisabled} isChecked={contentDisclosureToggle} onChange={onContentDisclosureToggle} />;
  };

  const getTextForTikTokAgreement = (): string => {
    if (isSelfBrand && !isPromotingThirdParty) {
      return window.i18next.t('pmwjs_agreement_music_tiktok');
    }

    return window.i18next.t('pmwjs_agreement_brand_and_music_tiktok');
  };

  const onTikTokPrivacySettingsSelected = (selectedOption: TikTokPrivacySettings): void => {
    if (selectedOption === TikTokPublicAccountPrivacySettings.SELF_ONLY || selectedOption === TikTokPrivateAccountPrivacySettings.SELF_ONLY) {
      setIsBrandSwitchDisabled(true);
      setIsPromotingThirdParty(false);
      setContentDisclosureToggle(false);
      setIsSelfBrand(false);
    } else {
      setIsBrandSwitchDisabled(false);
    }
  };

  const getContentDisclosureOptions = (): ReactElement<any> => {
    return (
      <div className={`flex-v-row ${styles.greyBox} ${contentDisclosureToggle ? styles.visible : ''}`}>
        <div className="flex-h-row spacing-m-b-4">
          <div className="spacing-m-l-2 spacing-m-r-3 spacing-m-t-1">
            <Checkbox type={CHECKBOX_TYPE.PRIMARY} isChecked={isSelfBrand} onClick={onIsSelfBrand} />
          </div>
          <div className="flex-v-row">
            <Text val={window.i18next.t('pmwjs_your_brand')} size={TextSize.XSMALL} bold className="spacing-m-b-1 content-body" />
            <Text val={window.i18next.t('pmwjs_video_labeled_promotional')} size={TextSize.XSMALL} className="content-sub-text" />
          </div>
        </div>
        <div className="flex-h-row spacing-m-b-4">
          <div className="spacing-m-l-2 spacing-m-r-3 spacing-m-t-1">
            <Checkbox type={CHECKBOX_TYPE.PRIMARY} isChecked={isPromotingThirdParty} onClick={onIsPromotingThirdParty} />
          </div>
          <div className="flex-v-row">
            <Text val={window.i18next.t('pmwjs_branded_content')} size={TextSize.XSMALL} bold className="spacing-m-b-1 content-body" />
            <Text val={window.i18next.t('pmwjs_your_video_paid_partnership')} size={TextSize.XSMALL} className="content-sub-text" />
          </div>
        </div>
        <div className="flex-h-row">
          <Icon icon="icon-info" className="spacing-m-r-2" type={IconType.NONE} size={IconSize.SIZE_ICON_20} />
          <Text className={styles.link} dangerouslySetInnerHTML val={getTextForTikTokAgreement()} size={TextSize.XSMALL} />
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (publishingParams) {
      setIsSelfBrand(publishingParams.isSelfBrand);
      setIsPromotingThirdParty(publishingParams.isPromotingThirdParty);
      setDisableComment(publishingParams.disableComment);
      setDisableStitch(publishingParams.disableStitch);
      setDisableDuet(publishingParams.disableDuet);

      if (publishingParams.isSelfBrand || publishingParams.isPromotingThirdParty) {
        setContentDisclosureToggle(true);
      }
    }

    void doRefreshTikTokOptions();
  }, []);

  return (
    <>
      <TiktokPrivacySelector account={account} onSelectOption={onTikTokPrivacySettingsSelected} isLoading={!isTikTokAPIQueried} />
      <FieldSpacer />
      {getPostMetaOptions()}
      <FieldSpacer />
      {getContentDisclosureToggle()}
      <FieldSpacer />
      {getContentDisclosureOptions()}
    </>
  );
}
